import React, { RefObject, useContext, useEffect, useMemo, useState } from 'react';
import { Group, Points, Scene, Vector3 } from 'three';

import { publishNotification } from '../../../../utils/notifications/publish-notification';
import { textUtils } from '../../../../utils/text/text';
import { isDefined } from '../../../../utils/variables';
import { coordinateToPoint3d } from '../../../../viewer/utilities/math-helpers';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';
import { SfmViewerMeasureTool } from './sfm-viewer-measure-tool';
import { SfmViewerTool, SfmViewerTooltip } from './sfm-viewer-tooltip';
import { CursorIntersectionResult } from './use-cursor';

type Props = {
  currentTool: SfmViewerTool;
  onToolChange?: (tool: SfmViewerTool) => void;
  cameraGroup?: Group;
  gcpObjectUuid?: string;
  intersection?: CursorIntersectionResult;
  cursorPosition?: Vector3;
  pointerRef?: RefObject<HTMLDivElement>;
  webGlScene?: Scene;
};

export const SfmViewerTools = ({
  currentTool,
  onToolChange,
  cameraGroup,
  gcpObjectUuid,
  intersection,
  cursorPosition,
  pointerRef,
  webGlScene,
}: Props) => {
  const { origo } = useContext(GeodataSpatialReferenceContext);
  const camerasObjectUuid = useMemo(() => {
    const cameraPoints = cameraGroup?.children.find((x) => x instanceof Points) as
      | Points
      | undefined;
    return cameraPoints?.uuid;
  }, [cameraGroup]);

  const [isMouseInContainer, setIsMouseInContainer] = useState(false);

  // Track mouse hover state over the pointerRef element
  useEffect(() => {
    if (!pointerRef?.current) {
      return;
    }

    const handleMouseEnter = () => {
      setIsMouseInContainer(true);
    };

    const handleMouseLeave = () => {
      setIsMouseInContainer(false);
    };

    const container = pointerRef.current;
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [pointerRef]);

  // Handle keyboard shortcuts: ESC to revert to select tool, Ctrl+C to copy coordinates
  // Only active when mouse is hovering over the viewer
  useEffect(() => {
    if (!isMouseInContainer) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // ESC key: revert to select tool (clears measurement state)
      if (event.key === 'Escape' && currentTool === 'measure') {
        onToolChange?.('select');
      }

      // Copy coordinate with Ctrl+C
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        if (currentTool === 'select' && isDefined(intersection)) {
          event.preventDefault();
          const coordinate = coordinateToPoint3d(intersection.coordinates);
          textUtils.copyTextToClipboard(`${coordinate.y}\t${coordinate.x}\t${coordinate.z}`);
          publishNotification('succ0903');
        } else if (currentTool === 'measure' && isDefined(cursorPosition)) {
          event.preventDefault();
          if (!isDefined(origo)) {
            return;
          }
          const x = cursorPosition.x + origo.x;
          const y = cursorPosition.y + origo.y;
          const z = cursorPosition.z + origo.z;
          textUtils.copyTextToClipboard(`${y}\t${x}\t${z}`);
          publishNotification('succ0903');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentTool, intersection, cursorPosition, onToolChange, isMouseInContainer, origo]);

  if (currentTool === 'select') {
    return (
      <SfmViewerTooltip
        camerasObjectUuid={camerasObjectUuid}
        gcpObjectUuid={gcpObjectUuid}
        intersection={intersection}
        pointerRef={pointerRef}
      />
    );
  }

  if (currentTool === 'measure') {
    return (
      <SfmViewerMeasureTool
        cursorPosition={cursorPosition}
        intersection={intersection}
        pointerRef={pointerRef}
        webGlScene={webGlScene}
        onToolChange={onToolChange}
      />
    );
  }

  return null;
};
